import { dueDate, EVENT_LABELS, RECEIVED_EVENTS, rulesFor, type EventKind, type IDeadlineRule, type IProgram } from '../../api/src/programs';
import type { FieldValue, IRecord } from '../lists/ListStore';

/**
 * Rows in the Events list. 'event': something that happened (a letter, a decision), on its date.
 * 'deadline': a date that follows from an event under a rule, calculated for a person to check
 * and confirm. 'instalment': a fee instalment due at a stage. 'signoff': a sign-off before submission.
 * 'task': a closing step. 'followup': a date to come back to the client (renewal, PR card, citizenship),
 * kept after the file is closed.
 */
export type RowKind = 'event' | 'deadline' | 'instalment' | 'signoff' | 'task' | 'followup';

const KINDS: RowKind[] = ['deadline', 'instalment', 'signoff', 'task', 'followup'];

export interface IEventView {
  rec: IRecord;
  key: string;
  title: string;
  kind: RowKind;
  /** For 'event' rows: which event. */
  event?: EventKind;
  eventKey?: string;
  eventDate?: string;
  receivedDate?: string;
  due?: string;
  ruleId?: string;
  ruleSource?: string;
  confirmedBy?: string;
  confirmedOn?: string;
  doneBy?: string;
  doneOn?: string;
  calendarEventId?: string;
  note?: string;
  applicationKey: string;
}

const str = (v: FieldValue): string => (v === undefined || v === null ? '' : String(v));
const opt = (v: FieldValue): string | undefined => (str(v) || undefined);

export function eventView(r: IRecord): IEventView {
  const f = r.fields;
  const raw: string = str(f.Kind);
  const kind: RowKind = KINDS.indexOf(raw as RowKind) !== -1 ? raw as RowKind : 'event';
  return {
    rec: r, key: r.key, title: str(f.Title), kind, event: kind === 'event' ? raw as EventKind : undefined, eventKey: opt(f.EventKey),
    eventDate: opt(f.EventDate), receivedDate: opt(f.ReceivedDate), due: opt(f.Due), ruleId: opt(f.RuleId), ruleSource: opt(f.RuleSource),
    confirmedBy: opt(f.ConfirmedBy), confirmedOn: opt(f.ConfirmedOn), doneBy: opt(f.DoneBy), doneOn: opt(f.DoneOn),
    calendarEventId: opt(f.CalendarEventId), note: opt(f.Note), applicationKey: str(f.ApplicationKey)
  };
}

/** What a person entered about a letter or event. */
export interface IEventInput {
  /** The date on the letter, or of the event. */
  letterDate?: string;
  /** For decisions and reasons (`RECEIVED_EVENTS`): the date the letter was received. */
  receivedDate?: string;
  /** Rules whose other period staff chose deliberately, by rule id. */
  ticked?: { [ruleId: string]: boolean };
  /** Dates typed from the letter (as stated, or the letter's own due date), by rule id. */
  stated?: { [ruleId: string]: string };
}

export function asksReceived(event: EventKind): boolean {
  return RECEIVED_EVENTS.indexOf(event) !== -1;
}

/**
 * The date deadlines are counted from: for decisions and reasons, the earlier of the date on the
 * letter and the date received (or whichever one was entered); otherwise the date of the event.
 */
export function countedFrom(event: EventKind, input: IEventInput): { date: string; words: string } | undefined {
  const letter: string | undefined = input.letterDate || undefined;
  const received: string | undefined = asksReceived(event) ? input.receivedDate || undefined : undefined;
  if (letter && received) {
    return letter <= received
      ? { date: letter, words: `Counted from the date on the letter (${letter}), the earlier of the two dates entered.` }
      : { date: received, words: `Counted from the date received (${received}), the earlier of the two dates entered.` };
  }
  if (received) {
    return { date: received, words: `Counted from the date received (${received}), the only date entered.` };
  }
  if (letter) {
    return { date: letter, words: asksReceived(event) ? `Counted from the date on the letter (${letter}), the only date entered.` : `Counted from ${letter}.` };
  }
  return undefined;
}

export interface IDeadlineLine {
  ruleId: string;
  label: string;
  /** The date, calculated or typed from the letter; undefined until a date the letter sets is entered. */
  due?: string;
  /** Calculated by the app (shown with the weekend note), rather than typed from the letter. */
  calculated: boolean;
  asStated?: { min: number; max: number };
  /** The letter usually states the due date: ask for it first. */
  letterFirst?: boolean;
  /** The other period staff can choose, and whether they did. */
  tick?: string;
  ticked: boolean;
  /** How the date was reached, for staff and History ("Counted from the date received…"). */
  how: string;
  /** Citation, caveat, how it was counted, and the review status: kept with the deadline. */
  source: string;
}

function lineFor(r: IDeadlineRule, event: EventKind, input: IEventInput): IDeadlineLine {
  const from = countedFrom(event, input);
  const ticked: boolean = !!(r.alt && input.ticked?.[r.id]);
  const stated: string | undefined = (r.asStated || r.letterFirst) ? input.stated?.[r.id] || undefined : undefined;
  const calc: string | undefined = from && !r.asStated ? dueDate(r, from.date, ticked) : undefined;
  const due: string | undefined = stated ?? calc;
  const days: number | undefined = ticked && r.alt ? r.alt.days : r.days;
  const how: string = stated
    ? 'The date written on the letter.'
    : r.letterFirst && calc
      ? `The letter gives no due date, so ${days} days were counted. ${from!.words}`
      : `${ticked ? `Staff ticked "${r.alt!.tick}": ${days} days. ` : ''}${from && calc ? from.words : ''}`.trim();
  const citation: string = ticked && r.alt ? r.alt.citation : r.citation;
  const caveat: string | undefined = ticked && r.alt?.caveat ? `${r.alt.caveat} ${r.caveat ?? ''}`.trim() : r.caveat;
  return {
    ruleId: r.id,
    label: r.label,
    due,
    calculated: !stated && !!calc,
    asStated: r.asStated,
    letterFirst: r.letterFirst,
    tick: r.alt?.tick,
    ticked,
    how,
    source: `${citation}.${caveat ? ` ${caveat}` : ''}${how ? ` ${how}` : ''}${r.review === 'pending' ? ' (Rule not yet reviewed by a lawyer.)' : ''}`
  };
}

/** The deadlines that follow an event, for this program (calculated, to be checked). */
export function deadlinesFor(program: IProgram, event: EventKind, input: IEventInput): IDeadlineLine[] {
  return rulesFor(program, event).map(r => lineFor(r, event, input));
}

export function eventLabel(e: EventKind): string {
  return EVENT_LABELS[e] ?? e;
}

/** Deadlines and instalments still to do, earliest first. */
export function openDues(rows: IEventView[]): IEventView[] {
  return rows
    .filter(r => (r.kind === 'deadline' || r.kind === 'instalment' || r.kind === 'followup') && r.due && !r.doneOn && r.rec.fields.Removed !== true)
    .sort((a, b) => (a.due ?? '').localeCompare(b.due ?? ''));
}

/** The earliest date still to meet, for the board and Today. */
export function nextDue(rows: IEventView[]): string | undefined {
  return openDues(rows)[0]?.due;
}

/** Days from one date to another (YYYY-MM-DD), negative when past. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
}

/** "Overdue by 3 days", "Due today", "Due in 5 days". */
export function dueWords(due: string, today: string): string {
  const d: number = daysBetween(today, due);
  return d < 0 ? `Overdue by ${-d} day${d === -1 ? '' : 's'}` : d === 0 ? 'Due today' : `Due in ${d} day${d === 1 ? '' : 's'}`;
}
